<template>
  <div class="member-login-page">
    <!-- Background Pattern -->
    <div class="login-bg-pattern"></div>

    <!-- Main Container -->
    <div class="login-wrapper">
      <!-- Back to Home Link -->
      <router-link to="/" class="back-home-link">
        <i class="bx bx-arrow-back"></i>
        <span>Back to Homepage</span>
      </router-link>

      <div class="login-container">
        <!-- Left Side - Branding Panel -->
        <div class="login-branding">
          <div class="branding-content">
            <!-- Logo -->
            <div class="logo-section">
              <img src="https://pona.paysol.africa/logos/pona.png" alt="PONA HEALTH-X Logo" class="logo-img">
              <div class="logo-text">
                <span class="logo-title">PONA HEALTH-X</span>
                <span class="logo-subtitle">Community Health Insurance</span>
              </div>
            </div>

            <!-- Title -->
            <h1 class="branding-title text-white">PONA HEALTH-X Member Portal</h1>
            <p class="branding-subtitle">
              Access your health insurance information, manage beneficiaries,
              and track your coverage status all in one place.
            </p>

            <!-- Features -->
            <div class="features-grid">
              <div class="feature-item" v-for="feature in portalFeatures" :key="feature.title">
                <div class="feature-icon">
                  <i :class="feature.icon"></i>
                </div>
                <div class="feature-text">
                  <span class="feature-title">{{ feature.title }}</span>
                  <span class="feature-desc">{{ feature.description }}</span>
                </div>
              </div>
            </div>

            <!-- Stats -->
            <div class="portal-stats">
              <div class="ps-item">
                <span class="ps-value">15M+</span>
                <span class="ps-label">Active Members</span>
              </div>
              <div class="ps-divider"></div>
              <div class="ps-item">
                <span class="ps-value">24/7</span>
                <span class="ps-label">Portal Access</span>
              </div>
              <div class="ps-divider"></div>
              <div class="ps-item">
                <span class="ps-value">100%</span>
                <span class="ps-label">Secure</span>
              </div>
            </div>

            <!-- Decorative Elements -->
            <div class="branding-decor">
              <div class="decor-circle circle-1"></div>
              <div class="decor-circle circle-2"></div>
              <div class="decor-circle circle-3"></div>
            </div>
          </div>
        </div>

        <!-- Right Side - Login Form -->
        <div class="login-form-section">
          <div class="form-container">
            <!-- Alert Messages -->
            <transition name="slide-down">
              <div v-if="alert.success" class="alert alert-success">
                <i class="bx bx-check-circle"></i>
                <span>{{ message }}</span>
                <button class="alert-close" @click="clearAlerts">
                  <i class="bx bx-x"></i>
                </button>
              </div>
            </transition>
            <transition name="slide-down">
              <div v-if="alert.error" class="alert alert-danger">
                <i class="bx bx-error-circle"></i>
                <span>{{ message }}</span>
                <button class="alert-close" @click="clearAlerts">
                  <i class="bx bx-x"></i>
                </button>
              </div>
            </transition>
            <transition name="slide-down">
              <div v-if="alert.info" class="alert alert-info">
                <i class="bx bx-info-circle"></i>
                <span>{{ message }}</span>
                <button class="alert-close" @click="clearAlerts">
                  <i class="bx bx-x"></i>
                </button>
              </div>
            </transition>

            <!-- Welcome Header -->
            <div class="form-header">
              <div class="welcome-icon">
                <i class="bx bx-user-circle"></i>
              </div>
              <h2 class="welcome-title">Welcome Back!</h2>
              <p class="welcome-subtitle">Sign in to access your PONA HEALTH-X member portal</p>
            </div>

            <!-- Login Method Tabs -->
            <div class="login-tabs">
              <button class="tab-btn"
                      :class="{ active: loginMethod === 'password' }"
                      @click="loginMethod = 'password'">
                <i class="bx bx-lock-alt"></i>
                <span>Password</span>
              </button>
              <button class="tab-btn"
                      :class="{ active: loginMethod === 'otp' }"
                      @click="loginMethod = 'otp'; resetOTPForm()">
                <i class="bx bx-message-square-dots"></i>
                <span>OTP</span>
              </button>
            </div>

            <!-- Password Login Form -->
            <form v-if="loginMethod === 'password'" @submit.prevent="loginWithPassword" class="login-form">
              <!-- Member Number -->
              <div class="form-group">
                <label class="form-label">
                  <i class="bx bx-id-card"></i>
                  PONA HEALTH-X Member Number
                </label>
                <div class="input-wrapper">
                  <input type="text"
                         v-model="loginForm.memberNumber"
                         placeholder="e.g., PONA72202507004"
                         class="form-input"
                         required>
                </div>
                <span class="input-hint">Enter your unique PONA HEALTH-X identification number</span>
              </div>

              <!-- Password -->
              <div class="form-group">
                <label class="form-label">
                  <i class="bx bx-lock-alt"></i>
                  Password
                </label>
                <div class="input-wrapper password-wrapper">
                  <input :type="showPassword ? 'text' : 'password'"
                         v-model="loginForm.password"
                         placeholder="Enter your password"
                         class="form-input"
                         required>
                  <button type="button" class="password-toggle" @click="togglePassword">
                    <i :class="showPassword ? 'bx bx-hide' : 'bx bx-show'"></i>
                  </button>
                </div>
              </div>

              <!-- Remember & Forgot -->
              <div class="form-options">
                <label class="checkbox-wrapper">
                  <input type="checkbox" v-model="loginForm.remember">
                  <span class="checkbox-custom"></span>
                  <span class="checkbox-label">Remember me</span>
                </label>
                <a href="#" class="forgot-link" @click.prevent="showForgotPassword">
                  Forgot password?
                </a>
              </div>

              <!-- Submit Button -->
              <button type="submit" class="submit-btn" :disabled="loading">
                <span v-if="loading" class="btn-loading">
                  <span class="spinner"></span>
                  Signing in...
                </span>
                <span v-else class="btn-content">
                  <i class="bx bx-log-in"></i>
                  Sign In
                </span>
              </button>
            </form>

            <!-- OTP Login Form -->
            <form v-if="loginMethod === 'otp'" @submit.prevent="otpSent ? verifyOTP() : sendOTP()" class="login-form">
              <!-- Before OTP Sent -->
              <template v-if="!otpSent">
                <!-- Member Number Only -->
                <div class="form-group">
                  <label class="form-label">
                    <i class="bx bx-id-card"></i>
                    PONA HEALTH-X Member Number
                  </label>
                  <div class="input-wrapper">
                    <input type="text"
                           v-model="otpForm.memberNumber"
                           placeholder="e.g., PONA72202507004"
                           class="form-input"
                           required>
                  </div>
                  <span class="input-hint">We'll send an OTP to your registered phone number and email</span>
                </div>
              </template>

              <!-- After OTP Sent -->
              <template v-else>
                <div class="otp-sent-notice">
                  <div class="notice-icon">
                    <i class="bx bx-message-check"></i>
                  </div>
                  <div class="notice-text">
                    <span class="notice-title">OTP Sent!</span>
                    <span class="notice-desc">
                      Check your
                      <strong v-if="maskedPhone">phone ({{ maskedPhone }})</strong>
                      <template v-if="maskedPhone && maskedEmail"> and </template>
                      <strong v-if="maskedEmail">email ({{ maskedEmail }})</strong>
                      for your verification code
                    </span>
                  </div>
                </div>

                <!-- OTP Input -->
                <div class="form-group">
                  <label class="form-label">
                    <i class="bx bx-dialpad"></i>
                    Enter OTP Code
                  </label>
                  <div class="otp-input-wrapper">
                    <input type="text"
                           v-model="otpForm.otpCode"
                           placeholder="000000"
                           class="form-input otp-input"
                           maxlength="6"
                           required>
                  </div>
                </div>

                <!-- Resend Link -->
                <div class="resend-section">
                  <span class="resend-text">Didn't receive the code?</span>
                  <button type="button" class="resend-btn" @click="resendOTP" :disabled="resendCooldown > 0">
                    {{ resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP' }}
                  </button>
                </div>
              </template>

              <!-- Submit Button -->
              <button type="submit" class="submit-btn" :disabled="loading">
                <span v-if="loading" class="btn-loading">
                  <span class="spinner"></span>
                  {{ otpSent ? 'Verifying...' : 'Sending OTP...' }}
                </span>
                <span v-else class="btn-content">
                  <i :class="otpSent ? 'bx bx-check-shield' : 'bx bx-send'"></i>
                  {{ otpSent ? 'Verify & Sign In' : 'Send OTP' }}
                </span>
              </button>

              <!-- Change Method -->
              <button v-if="otpSent" type="button" class="secondary-btn" @click="resetOTPForm">
                <i class="bx bx-arrow-back"></i>
                Use Different Member Number
              </button>
            </form>

            <!-- Divider -->
            <div class="form-divider">
              <span>or</span>
            </div>

            <!-- Not a Member Section -->
            <div class="not-member-section">
              <p class="not-member-text">Not a PONA HEALTH-X member yet?</p>
              <router-link to="/enrollment" class="enroll-btn">
                <i class="bx bx-user-plus"></i>
                Learn How to Enroll
              </router-link>
            </div>

            <!-- Staff Login -->
            <div class="staff-section">
              <span class="staff-text">Are you a staff member?</span>
              <button class="staff-btn" @click="goToStaffLogin">
                <i class="bx bx-briefcase"></i>
                Staff Login
              </button>
            </div>

            <!-- Help Section -->
            <div class="help-section">
              <div class="help-icon">
                <i class="bx bx-help-circle"></i>
              </div>
              <div class="help-content">
                <span class="help-title">Need Help?</span>
                <span class="help-desc">
                  Contact your local health office or call our support line
                </span>
              </div>
              <a href="tel:+254700000000" class="help-btn">
                <i class="bx bx-phone"></i>
                Call Us
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Links -->
      <div class="login-footer">
        <div class="footer-links">
          <router-link to="/faqs">FAQs</router-link>
          <router-link to="/contact">Contact Us</router-link>
          <router-link to="/about">About PONA HEALTH-X</router-link>
        </div>
        <p class="footer-copyright">© 2025 PONA HEALTH-X. All rights reserved.</p>
      </div>
    </div>

    <!-- Forgot Password Modal -->
    <transition name="fade">
      <div class="modal-overlay" v-if="showResetModal" @click.self="closeResetModal">
        <div class="modal-container">
          <div class="modal-header">
            <div class="modal-icon">
              <i class="bx bx-lock-open"></i>
            </div>
            <h3 class="modal-title">{{ resetStep === 'request' ? 'Reset Password' : 'Set New Password' }}</h3>
            <button class="modal-close" @click="closeResetModal">
              <i class="bx bx-x"></i>
            </button>
          </div>
          <div class="modal-body">
            <!-- Step 1: Request OTP -->
            <template v-if="resetStep === 'request'">
              <p class="modal-desc">
                Enter your PONA HEALTH-X member number and registered phone to receive password reset OTP.
              </p>

              <form @submit.prevent="requestPasswordReset">
                <div class="form-group">
                  <label class="form-label">
                    <i class="bx bx-id-card"></i>
                    PONA HEALTH-X Member Number
                  </label>
                  <div class="input-wrapper">
                    <input type="text"
                           v-model="resetForm.memberNumber"
                           placeholder="e.g., PONA72202507004"
                           class="form-input"
                           required>
                  </div>
                </div>

                <div class="form-group">
                  <label class="form-label">
                    <i class="bx bx-phone"></i>
                    Phone Number
                  </label>
                  <div class="input-wrapper">
                    <input type="tel"
                           v-model="resetForm.phone"
                           placeholder="e.g., 0700123456"
                           class="form-input"
                           required>
                  </div>
                </div>

                <button type="submit" class="submit-btn" :disabled="loading">
                  <span v-if="loading" class="btn-loading">
                    <span class="spinner"></span>
                    Sending...
                  </span>
                  <span v-else class="btn-content">
                    <i class="bx bx-send"></i>
                    Send Reset OTP
                  </span>
                </button>
              </form>
            </template>

            <!-- Step 2: Reset Password with OTP -->
            <template v-else>
              <p class="modal-desc">
                Enter the OTP sent to your phone and set your new password.
              </p>

              <form @submit.prevent="resetPassword">
                <div class="form-group">
                  <label class="form-label">
                    <i class="bx bx-dialpad"></i>
                    Enter OTP Code
                  </label>
                  <div class="input-wrapper">
                    <input type="text"
                           v-model="resetForm.otpCode"
                           placeholder="000000"
                           class="form-input"
                           maxlength="6"
                           required>
                  </div>
                </div>

                <div class="form-group">
                  <label class="form-label">
                    <i class="bx bx-lock-alt"></i>
                    New Password
                  </label>
                  <div class="input-wrapper">
                    <input type="password"
                           v-model="resetForm.newPassword"
                           placeholder="Enter new password"
                           class="form-input"
                           required>
                  </div>
                </div>

                <div class="form-group">
                  <label class="form-label">
                    <i class="bx bx-lock-alt"></i>
                    Confirm Password
                  </label>
                  <div class="input-wrapper">
                    <input type="password"
                           v-model="resetForm.confirmPassword"
                           placeholder="Confirm new password"
                           class="form-input"
                           required>
                  </div>
                </div>

                <button type="submit" class="submit-btn" :disabled="loading">
                  <span v-if="loading" class="btn-loading">
                    <span class="spinner"></span>
                    Resetting...
                  </span>
                  <span v-else class="btn-content">
                    <i class="bx bx-check"></i>
                    Reset Password
                  </span>
                </button>

                <button type="button" class="secondary-btn" @click="resetStep = 'request'">
                  <i class="bx bx-arrow-back"></i>
                  Back
                </button>
              </form>
            </template>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";
import router from "@/router";

export default {
  name: 'PonaHealthMemberLogin',

  data() {
    return {
      loading: false,
      showPassword: false,
      otpSent: false,
      showResetModal: false,
      loginMethod: 'password',
      resendCooldown: 0,

      loginForm: {
        memberNumber: '',
        password: '',
        remember: false
      },

      otpForm: {
        memberNumber: '',
        otpCode: ''
      },

      otpResponse: null, // Store OTP response data (masked phone/email)

      resetForm: {
        memberNumber: '',
        phone: '',
        otpCode: '',
        newPassword: '',
        confirmPassword: ''
      },

      resetStep: 'request', // 'request' or 'reset'

      alert: {
        success: false,
        error: false,
        info: false
      },
      message: '',

      portalFeatures: [
        {
          icon: 'bx bx-shield-check',
          title: 'View Coverage',
          description: 'Check your benefits and coverage status'
        },
        {
          icon: 'bx bx-receipt',
          title: 'Payment History',
          description: 'Track all premium payments'
        },
        {
          icon: 'bx bx-group',
          title: 'Manage Beneficiaries',
          description: 'Add or update family members'
        },
        {
          icon: 'bx bx-id-card',
          title: 'Digital Card',
          description: 'Download your PONA HEALTH-X card'
        }
      ]
    };
  },

  computed: {
    maskedPhone() {
      return this.otpResponse?.maskedPhone || '';
    },
    maskedEmail() {
      return this.otpResponse?.maskedEmail || '';
    }
  },

  mounted() {
    this.checkRememberedLogin();
  },

  methods: {
    checkRememberedLogin() {
      const rememberedMember = localStorage.getItem('rememberedMemberNumber');
      if (rememberedMember) {
        this.loginForm.memberNumber = rememberedMember;
        this.loginForm.remember = true;
      }
    },

    async loginWithPassword() {
      if (!this.loginForm.memberNumber || !this.loginForm.password) {
        this.showAlert('error', 'Please enter both member number and password');
        return;
      }

      this.loading = true;
      this.clearAlerts();

      const data = {
        "function": "memberLoginPassword",
        "memberNumber": this.loginForm.memberNumber,
        "password": this.loginForm.password
      };

      try {
        const res = await executeJson(data, authUrl);
        this.loading = false;

        if (res.data.success) {
          // Store authentication token
          localStorage.setItem("authToken", res.data.data.token);

          // Store member information
          const memberData = res.data.data.member;
          sessionStorage.setItem("memberLoggedIn", true);
          sessionStorage.setItem("memberId", memberData.memberId);
          sessionStorage.setItem("memberNumber", memberData.memberNumber);
          sessionStorage.setItem("memberName", memberData.fullName);
          sessionStorage.setItem("memberData", JSON.stringify(memberData));

          // Remember member number if checkbox is checked
          if (this.loginForm.remember) {
            localStorage.setItem('rememberedMemberNumber', this.loginForm.memberNumber);
          } else {
            localStorage.removeItem('rememberedMemberNumber');
          }

          this.showAlert('success', res.data.message);

          // Check if password change is required
          if (memberData.mustChangePassword) {
            setTimeout(() => {
              router.push('/change-password');
            }, 1500);
          } else {
            setTimeout(() => {
              router.push('/member-dashboard');
            }, 1500);
          }
        } else {
          this.showAlert('error', res.data.message);
        }
      } catch (error) {
        this.loading = false;
        this.showAlert('error', error.message || 'Login failed. Please try again.');
      }
    },

    async sendOTP() {
      if (!this.otpForm.memberNumber) {
        this.showAlert('error', 'Please enter your member number');
        return;
      }

      this.loading = true;
      this.clearAlerts();

      const data = {
        "function": "memberSendOTP",
        "memberNumber": this.otpForm.memberNumber
      };

      try {
        const res = await executeJson(data, authUrl);
        this.loading = false;

        if (res.data.success) {
          this.otpSent = true;
          this.otpResponse = res.data.data; // Store response with masked phone/email
          this.showAlert('success', res.data.message);
          this.startResendCooldown();
        } else {
          this.showAlert('error', res.data.message);
        }
      } catch (error) {
        this.loading = false;
        this.showAlert('error', error.message || 'Failed to send OTP. Please try again.');
      }
    },

    async verifyOTP() {
      if (!this.otpForm.otpCode || this.otpForm.otpCode.length !== 6) {
        this.showAlert('error', 'Please enter the 6-digit OTP code');
        return;
      }

      this.loading = true;
      this.clearAlerts();

      const data = {
        "function": "memberVerifyOTP",
        "memberNumber": this.otpForm.memberNumber,
        "otpCode": this.otpForm.otpCode
      };

      try {
        const res = await executeJson(data, authUrl);
        this.loading = false;

        if (res.data.success) {
          // Store authentication token
          localStorage.setItem("authToken", res.data.data.token);

          // Store member information
          const memberData = res.data.data.member;
          sessionStorage.setItem("memberLoggedIn", true);
          sessionStorage.setItem("memberId", memberData.memberId);
          sessionStorage.setItem("memberNumber", memberData.memberNumber);
          sessionStorage.setItem("memberName", memberData.fullName);
          sessionStorage.setItem("memberData", JSON.stringify(memberData));

          this.showAlert('success', res.data.message);

          // Check if user should set password
          if (res.data.data.promptSetPassword) {
            setTimeout(() => {
              router.push('/set-password');
            }, 1500);
          } else {
            setTimeout(() => {
              router.push('/member-dashboard');
            }, 1500);
          }
        } else {
          this.showAlert('error', res.data.message);
        }
      } catch (error) {
        this.loading = false;
        this.showAlert('error', error.message || 'OTP verification failed. Please try again.');
      }
    },

    resendOTP() {
      if (this.resendCooldown > 0) return;

      this.otpForm.otpCode = '';
      this.otpSent = false;
      this.otpResponse = null;
      this.sendOTP();
    },

    startResendCooldown() {
      this.resendCooldown = 60;
      const timer = setInterval(() => {
        this.resendCooldown--;
        if (this.resendCooldown <= 0) {
          clearInterval(timer);
        }
      }, 1000);
    },

    resetOTPForm() {
      this.otpSent = false;
      this.otpForm.otpCode = '';
      this.otpResponse = null;
    },

    togglePassword() {
      this.showPassword = !this.showPassword;
    },

    showForgotPassword() {
      this.showResetModal = true;
      this.resetStep = 'request';
      this.resetForm = {
        memberNumber: '',
        phone: '',
        otpCode: '',
        newPassword: '',
        confirmPassword: ''
      };
    },

    closeResetModal() {
      this.showResetModal = false;
      this.resetStep = 'request';
      this.resetForm = {
        memberNumber: '',
        phone: '',
        otpCode: '',
        newPassword: '',
        confirmPassword: ''
      };
    },

    async requestPasswordReset() {
      if (!this.resetForm.memberNumber || !this.resetForm.phone) {
        this.showAlert('error', 'Please enter both member number and phone number');
        return;
      }

      this.loading = true;

      const data = {
        "function": "memberForgotPassword",
        "memberNumber": this.resetForm.memberNumber,
        "phoneNumber": this.resetForm.phone
      };

      try {
        const res = await executeJson(data, authUrl);
        this.loading = false;

        if (res.data.success) {
          this.resetStep = 'reset';
          this.showAlert('success', res.data.message);
        } else {
          this.showAlert('error', res.data.message);
        }
      } catch (error) {
        this.loading = false;
        this.showAlert('error', error.message || 'Failed to send reset OTP. Please try again.');
      }
    },

    async resetPassword() {
      if (!this.resetForm.otpCode || !this.resetForm.newPassword || !this.resetForm.confirmPassword) {
        this.showAlert('error', 'Please fill in all fields');
        return;
      }

      if (this.resetForm.newPassword !== this.resetForm.confirmPassword) {
        this.showAlert('error', 'Passwords do not match');
        return;
      }

      if (this.resetForm.newPassword.length < 8) {
        this.showAlert('error', 'Password must be at least 8 characters long');
        return;
      }

      this.loading = true;

      const data = {
        "function": "memberResetPassword",
        "memberNumber": this.resetForm.memberNumber,
        "otpCode": this.resetForm.otpCode,
        "newPassword": this.resetForm.newPassword,
        "confirmPassword": this.resetForm.confirmPassword
      };

      try {
        const res = await executeJson(data, authUrl);
        this.loading = false;

        if (res.data.success) {
          this.showAlert('success', res.data.message);
          setTimeout(() => {
            this.closeResetModal();
            this.loginMethod = 'password';
          }, 2000);
        } else {
          this.showAlert('error', res.data.message);
        }
      } catch (error) {
        this.loading = false;
        this.showAlert('error', error.message || 'Failed to reset password. Please try again.');
      }
    },

    showAlert(type, msg) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = msg;

      if (type !== 'error') {
        setTimeout(() => {
          this.clearAlerts();
        }, 5000);
      }
    },

    clearAlerts() {
      this.alert = { success: false, error: false, info: false };
      this.message = '';
    },

    goToStaffLogin() {
      router.push('/login');
    }
  }
};
</script>


<style scoped>
/* ========================================================================= */
/* MEMBER LOGIN PAGE - PONA HEALTH-X DESIGN */
/* ========================================================================= */

.member-login-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;
  overflow: hidden;
}

/* Background Pattern */
.login-bg-pattern {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
      radial-gradient(circle at 20% 80%, rgba(44, 90, 160, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(74, 144, 226, 0.05) 0%, transparent 50%),
      url('data:image/svg+xml,<svg width="60" height="60" xmlns="http://www.w3.org/2000/svg"><circle cx="30" cy="30" r="1" fill="rgba(44,90,160,0.08)"/></svg>');
  pointer-events: none;
}

/* ========================================================================= */
/* WRAPPER & CONTAINER */
/* ========================================================================= */
.login-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  z-index: 1;
}

.back-home-link {
  position: absolute;
  top: 2rem;
  left: 2rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #2c5aa0;
  text-decoration: none;
  font-weight: 600;
  padding: 0.75rem 1.25rem;
  background: white;
  border-radius: 50px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.back-home-link:hover {
  transform: translateX(-5px);
  box-shadow: 0 8px 25px rgba(44, 90, 160, 0.15);
}

.back-home-link i {
  font-size: 1.25rem;
}

.login-container {
  display: flex;
  width: 100%;
  max-width: 1100px;
  background: white;
  border-radius: 2rem;
  overflow: hidden;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.12);
}

/* ========================================================================= */
/* LEFT PANEL - BRANDING */
/* ========================================================================= */
.login-branding {
  flex: 1;
  background: linear-gradient(135deg, #1e3f70 0%, #2c5aa0 50%, #4a90e2 100%);
  padding: 3rem;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.branding-content {
  position: relative;
  z-index: 2;
  color: white;
}

/* Logo */
.logo-section {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
}

.logo-img {
  height: 70px;
  background: white;
  padding: 8px;
  border-radius: 12px;
}

.logo-text {
  display: flex;
  flex-direction: column;
}

.logo-title {
  font-size: 1.25rem;
  font-weight: 700;
}

.logo-subtitle {
  font-size: 0.85rem;
  opacity: 0.8;
}

/* Title */
.branding-title {
  font-size: 2.25rem;
  font-weight: 800;
  margin-bottom: 1rem;
  line-height: 1.2;
}

.branding-subtitle {
  font-size: 1rem;
  opacity: 0.85;
  line-height: 1.7;
  margin-bottom: 2.5rem;
  max-width: 380px;
}

/* Features */
.features-grid {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-bottom: 2.5rem;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.feature-item:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateX(10px);
}

.feature-icon {
  width: 45px;
  height: 45px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.feature-text {
  display: flex;
  flex-direction: column;
}

.feature-title {
  font-weight: 700;
  font-size: 0.95rem;
}

.feature-desc {
  font-size: 0.8rem;
  opacity: 0.75;
}

/* Stats */
.portal-stats {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  backdrop-filter: blur(10px);
}

.ps-item {
  text-align: center;
}

.ps-value {
  display: block;
  font-size: 1.5rem;
  font-weight: 900;
}

.ps-label {
  font-size: 0.75rem;
  opacity: 0.75;
}

.ps-divider {
  width: 1px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
}

/* Decorative Elements */
.branding-decor {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 300px;
  pointer-events: none;
}

.decor-circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
}

.circle-1 {
  width: 300px;
  height: 300px;
  top: -100px;
  right: -100px;
}

.circle-2 {
  width: 200px;
  height: 200px;
  bottom: 50px;
  right: -50px;
}

.circle-3 {
  width: 100px;
  height: 100px;
  bottom: 200px;
  right: 100px;
}

/* ========================================================================= */
/* RIGHT PANEL - FORM */
/* ========================================================================= */
.login-form-section {
  flex: 1;
  padding: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 500px;
}

.form-container {
  width: 100%;
  max-width: 400px;
}

/* Alerts */
.alert {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-radius: 1rem;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  position: relative;
}

.alert i:first-child {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.alert-success {
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  color: #155724;
  border-left: 4px solid #28a745;
}

.alert-danger {
  background: linear-gradient(135deg, #f8d7da 0%, #f1b0b7 100%);
  color: #721c24;
  border-left: 4px solid #dc3545;
}

.alert-info {
  background: linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%);
  color: #0c5460;
  border-left: 4px solid #17a2b8;
}

.alert-close {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  opacity: 0.6;
  padding: 0.25rem;
}

.alert-close:hover {
  opacity: 1;
}

/* Welcome Header */
.form-header {
  text-align: center;
  margin-bottom: 2rem;
}

.welcome-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 2.5rem;
  color: #2c5aa0;
}

.welcome-title {
  font-size: 1.75rem;
  font-weight: 800;
  color: #1e3f70;
  margin-bottom: 0.5rem;
}

.welcome-subtitle {
  color: #6c757d;
  font-size: 0.95rem;
}

/* Login Tabs */
.login-tabs {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
}

.tab-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
  border: 2px solid #e9ecef;
  background: white;
  border-radius: 1rem;
  font-weight: 600;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tab-btn:hover {
  border-color: #bbdefb;
  color: #2c5aa0;
}

.tab-btn.active {
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%);
  border-color: transparent;
  color: white;
}

.tab-btn i {
  font-size: 1.25rem;
}

/* Form Elements */
.login-form {
  margin-bottom: 1.5rem;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #1e3f70;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.form-label i {
  font-size: 1.1rem;
  color: #2c5aa0;
}

.input-wrapper {
  position: relative;
}

.form-input {
  width: 100%;
  padding: 1rem 1.25rem;
  border: 2px solid #e9ecef;
  border-radius: 1rem;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: #fafbfc;
}

.form-input:focus {
  outline: none;
  border-color: #2c5aa0;
  background: white;
  box-shadow: 0 0 0 4px rgba(44, 90, 160, 0.1);
}

.form-input::placeholder {
  color: #adb5bd;
}

.input-hint {
  display: block;
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 0.5rem;
}

/* Password Toggle */
.password-wrapper {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6c757d;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.25rem;
  transition: all 0.3s ease;
}

.password-toggle:hover {
  color: #2c5aa0;
}

/* Form Options */
.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.checkbox-wrapper input {
  display: none;
}

.checkbox-custom {
  width: 20px;
  height: 20px;
  border: 2px solid #e9ecef;
  border-radius: 6px;
  transition: all 0.3s ease;
  position: relative;
}

.checkbox-wrapper input:checked + .checkbox-custom {
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%);
  border-color: transparent;
}

.checkbox-wrapper input:checked + .checkbox-custom::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 0.75rem;
  font-weight: bold;
}

.checkbox-label {
  font-size: 0.9rem;
  color: #495057;
}

.forgot-link {
  color: #2c5aa0;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.forgot-link:hover {
  color: #1e3f70;
}

/* Submit Button */
.submit-btn {
  width: 100%;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%);
  border: none;
  border-radius: 1rem;
  color: white;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(44, 90, 160, 0.3);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-content,
.btn-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-content i {
  font-size: 1.25rem;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.secondary-btn {
  width: 100%;
  padding: 0.875rem 1.25rem;
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 1rem;
  color: #495057;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.secondary-btn:hover {
  border-color: #2c5aa0;
  color: #2c5aa0;
}

/* OTP Section */
.otp-sent-notice {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  border-radius: 1rem;
  margin-bottom: 1.5rem;
}

.notice-icon {
  width: 50px;
  height: 50px;
  background: #28a745;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  color: white;
  flex-shrink: 0;
}

.notice-title {
  display: block;
  font-weight: 700;
  color: #155724;
}

.notice-desc {
  font-size: 0.85rem;
  color: #155724;
}

.otp-input {
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: 0.5rem;
}

.resend-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.resend-text {
  color: #6c757d;
  font-size: 0.9rem;
}

.resend-btn {
  background: none;
  border: none;
  color: #2c5aa0;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.9rem;
}

.resend-btn:disabled {
  color: #adb5bd;
  cursor: not-allowed;
}

/* Divider */
.form-divider {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 1.5rem 0;
}

.form-divider::before,
.form-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e9ecef;
}

.form-divider span {
  color: #adb5bd;
  font-size: 0.85rem;
  font-weight: 500;
}

/* Not Member Section */
.not-member-section {
  text-align: center;
  margin-bottom: 1.25rem;
}

.not-member-text {
  color: #6c757d;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
}

.enroll-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #28a745 0%, #5cb85c 100%);
  color: white;
  text-decoration: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.enroll-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(40, 167, 69, 0.3);
}

/* Staff Section */
.staff-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 1rem;
  margin-bottom: 1.25rem;
}

.staff-text {
  color: #6c757d;
  font-size: 0.9rem;
}

.staff-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 50px;
  color: #495057;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.staff-btn:hover {
  border-color: #2c5aa0;
  color: #2c5aa0;
}

/* Help Section */
.help-section {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: linear-gradient(135deg, #fff3cd 0%, #ffeeba 100%);
  border-radius: 1rem;
  border-left: 4px solid #ffc107;
}

.help-icon {
  width: 45px;
  height: 45px;
  background: #ffc107;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #1e3f70;
  flex-shrink: 0;
}

.help-content {
  flex: 1;
}

.help-title {
  display: block;
  font-weight: 700;
  color: #856404;
  font-size: 0.9rem;
}

.help-desc {
  font-size: 0.8rem;
  color: #856404;
}

.help-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #1e3f70;
  color: white;
  text-decoration: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.85rem;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.help-btn:hover {
  background: #2c5aa0;
  transform: translateY(-2px);
}

/* ========================================================================= */
/* FOOTER */
/* ========================================================================= */
.login-footer {
  text-align: center;
  margin-top: 2rem;
}

.footer-links {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 1rem;
}

.footer-links a {
  color: #6c757d;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.footer-links a:hover {
  color: #2c5aa0;
}

.footer-copyright {
  color: #adb5bd;
  font-size: 0.85rem;
}

/* ========================================================================= */
/* MODAL */
/* ========================================================================= */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-container {
  background: white;
  border-radius: 1.5rem;
  width: 100%;
  max-width: 450px;
  overflow: hidden;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.modal-icon {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #2c5aa0;
}

.modal-title {
  flex: 1;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e3f70;
  margin: 0;
}

.modal-close {
  width: 40px;
  height: 40px;
  background: #f8f9fa;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.3s ease;
}

.modal-close:hover {
  background: #e9ecef;
  color: #dc3545;
}

.modal-body {
  padding: 1.5rem;
}

.modal-desc {
  color: #6c757d;
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
}

/* ========================================================================= */
/* TRANSITIONS */
/* ========================================================================= */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ========================================================================= */
/* RESPONSIVE */
/* ========================================================================= */
@media (max-width: 992px) {
  .login-container {
    flex-direction: column;
    max-width: 500px;
  }

  .login-branding {
    padding: 2rem;
  }

  .branding-title {
    font-size: 1.75rem;
  }

  .features-grid {
    display: none;
  }

  .portal-stats {
    justify-content: center;
  }

  .login-form-section {
    max-width: 100%;
    padding: 2rem;
  }

  .back-home-link {
    position: relative;
    top: 0;
    left: 0;
    margin-bottom: 1rem;
  }
}

@media (max-width: 768px) {
  .login-wrapper {
    padding: 1rem;
  }

  .login-branding {
    padding: 1.5rem;
  }

  .logo-img {
    height: 50px;
  }

  .branding-title {
    font-size: 1.5rem;
  }

  .branding-subtitle {
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
  }

  .portal-stats {
    padding: 1rem;
    gap: 1rem;
  }

  .ps-value {
    font-size: 1.25rem;
  }

  .login-form-section {
    padding: 1.5rem;
  }

  .welcome-title {
    font-size: 1.5rem;
  }

  .help-section {
    flex-direction: column;
    text-align: center;
  }

  .footer-links {
    gap: 1rem;
  }
}

@media (max-width: 576px) {
  .login-tabs {
    flex-direction: column;
  }

  .staff-section {
    flex-direction: column;
    gap: 0.75rem;
  }

  .form-options {
    flex-direction: column;
    gap: 0.75rem;
    align-items: flex-start;
  }
}
</style>